import { NodeModel } from "@minoru/react-dnd-treeview";
import AccountTree from "@mui/icons-material/AccountTree";
import SearchIcon from "@mui/icons-material/Search";
import Settings from "@mui/icons-material/Settings";
import {
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { ShelfSearchResult, useShelfSearch } from "../hooks/useShelfSearch";
import { FileProperties } from "../models/FileProperties";

interface ShelfSearchInputProps {
  shelvesNodes: NodeModel<FileProperties>[];
  onSearchResultClick: (result: ShelfSearchResult) => void;
}

export const ShelfSearchInput = ({
  shelvesNodes,
  onSearchResultClick,
}: ShelfSearchInputProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const {
    searchTerm,
    searchResults,
    showSearchResults,
    focusedSearchIndex,
    searchResultsRefs,
    handleSearchChange,
    handleKeyDown,
    clearSearch,
  } = useShelfSearch({ shelfNodes: shelvesNodes });

  const handleSearchResultClick = (result: ShelfSearchResult) => {
    onSearchResultClick(result);
    clearSearch();
    setAnchorEl(null);
  };

  const handleKeyDownWithResultSelection = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    const selectedResult = handleKeyDown(event);
    if (selectedResult) {
      handleSearchResultClick(selectedResult);
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    handleSearchChange(event);
    if (!anchorEl && searchInputRef.current) {
      setAnchorEl(searchInputRef.current);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <div
      onKeyDown={handleKeyDownWithResultSelection}
      style={{ marginBottom: 16 }}
    >
      <TextField
        placeholder="Search shelves (min. 2 characters)..."
        variant="outlined"
        size="small"
        fullWidth
        value={searchTerm}
        onChange={handleSearchInputChange}
        inputRef={searchInputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={showSearchResults && Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: { width: anchorEl?.offsetWidth, maxHeight: 300 },
        }}
      >
        <MenuList dense>
          {searchResults.map((result, index) => (
            <MenuItem
              key={result.id}
              ref={(el) => {
                if (searchResultsRefs.current) {
                  searchResultsRefs.current[index] = el;
                }
              }}
              selected={index === focusedSearchIndex}
              onClick={() => handleSearchResultClick(result)}
            >
              <ListItemIcon>
                {result.hasChildren || result.isDroppable ? (
                  <AccountTree color="primary" fontSize="small" />
                ) : (
                  <Settings color="primary" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={result.name}
                secondary={result.path}
                secondaryTypographyProps={{
                  variant: "caption",
                  color: "textSecondary",
                  style: { fontStyle: "italic" },
                }}
              />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </div>
  );
};
